import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Box,
  Avatar,
  TextField,
  TableSortLabel,
  Grid,
} from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard, StatsCard } from '../components/layout/EnhancedCard';
import DivineOrbIcon from '../components/icons/DivineOrbIcon';
import { convertDDSToWebp } from '../utils/imageUtils';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../store/store';
import ItemDetailsPanel from '../components/item/ItemDetailsPanel';
import type { Item } from '../store/slices/itemsSlice';
import GameLeagueSelector from '../components/GameLeagueSelector';
import { selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';
import { fetchItems, selectAllItems, selectItemsStatus, selectItemsError, clearItemsError } from '../store/slices/itemsSlice';
import { getLatestExchangeRates, findCurrencyItemIds, type ItemExchangeRate } from '../services/marketAnalysisService';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';

const ItemInventoryPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const items = useSelector(selectAllItems);
  const status = useSelector(selectItemsStatus);
  const error = useSelector(selectItemsError);

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // State for item details panel
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // State for sorting
  type SortField = 'name' | 'type' | 'value' | 'quantity';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // State for pricing data
  const [priceMap, setPriceMap] = useState<Record<string, number>>({});
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesError, setPricesError] = useState<string | null>(null);

  // Handler for opening the item details panel
  const handleItemClick = (item: Item) => {
    setSelectedItem(item);
    setDetailsPanelOpen(true);
  };

  // Handler for closing the item details panel
  const handleCloseDetailsPanel = () => {
    setDetailsPanelOpen(false);
  };

  // Handler for sorting
  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  useEffect(() => {
    // Clear any previous errors
    dispatch(clearItemsError());

    // Fetch items whenever game or league changes
    dispatch(fetchItems());
  }, [dispatch, selectedGame, selectedLeague]); // Re-fetch when filters change

  // Fetch ALL pricing data once items are loaded
  useEffect(() => {
    if (status === 'succeeded' && items.length > 0 && selectedGame && selectedLeague) {
      setPricesLoading(true);
      setPricesError(null);

      // Get ALL exchange rates for this game/league in one bulk request
      getLatestExchangeRates(selectedGame, selectedLeague)
        .then((rates: ItemExchangeRate[]) => {
          // Find Divine Orb ID from the exchange rates
          const { divineOrbId } = findCurrencyItemIds(rates);

          if (!divineOrbId) {
            setPricesError('Divine Orb currency not found in exchange rates');
            setPriceMap({});
            return;
          }

          // Build price map from ALL exchange rates
          const newPriceMap: Record<string, number> = {};

          // Process all exchange rates to build comprehensive price map
          rates.forEach(rate => {
            // Skip if rate doesn't have proper exchange rate calculation
            if (!rate.quantityFrom || !rate.quantityTo) return;

            const exchangeRate = rate.quantityTo / rate.quantityFrom;

            // Case 1: Item -> Divine Orb (direct pricing)
            if (rate.itemToId === divineOrbId && rate.itemFromId !== divineOrbId) {
              newPriceMap[rate.itemFromId] = exchangeRate;
            }
            // Case 2: Divine Orb -> Item (inverse pricing)
            else if (rate.itemFromId === divineOrbId && rate.itemToId !== divineOrbId) {
              if (exchangeRate > 0) {
                newPriceMap[rate.itemToId] = 1 / exchangeRate;
              }
            }
          });

          console.log(`Built price map for ${Object.keys(newPriceMap).length} items`);
          setPriceMap(newPriceMap);
        })
        .catch((error: any) => {
          console.error('Error fetching pricing data:', error);
          setPricesError(error?.response?.data?.message || error.message || 'Failed to load pricing data');
        })
        .finally(() => {
          setPricesLoading(false);
        });
    } else {
      // Reset pricing state when items are not loaded
      setPriceMap({});
      setPricesError(null);
    }
  }, [status, items, selectedGame, selectedLeague]);

  const filteredItems = useMemo(() => {
    let filtered = items;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'type':
          aValue = a.item_type?.toLowerCase() || '';
          bValue = b.item_type?.toLowerCase() || '';
          break;
        case 'quantity':
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case 'value':
          const aPricePerUnit = priceMap[a.id] || 0;
          const bPricePerUnit = priceMap[b.id] || 0;
          aValue = aPricePerUnit * (a.quantity || 0);
          bValue = bPricePerUnit * (b.quantity || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [items, searchQuery, sortField, sortDirection, priceMap]);

  // Calculate total portfolio value
  const totalPortfolioValue = useMemo(() => {
    if (!items.length || !Object.keys(priceMap).length) return 0;

    return items.reduce((total, item) => {
      const unitPrice = priceMap[item.id];
      if (typeof unitPrice === 'number' && item.quantity) {
        return total + unitPrice * item.quantity;
      }
      return total;
    }, 0);
  }, [items, priceMap]);

  let content;

  if (status === 'loading') {
    content = (
      <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' minHeight='300px' gap={2}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant='h6' color='text.secondary'>
          Loading items...
        </Typography>
      </Box>
    );
  } else if (status === 'succeeded') {
    if (filteredItems.length === 0) {
      content = (
        <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' minHeight='300px' textAlign='center' gap={2}>
          <InventoryIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.5 }} />
          <Typography variant='h5' color='text.secondary'>
            {searchQuery ? 'No matching items found' : 'No items found'}
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            {searchQuery
              ? 'Try adjusting your search criteria or check different filters.'
              : 'Items will appear here once they are detected by your characters.'}
          </Typography>
        </Box>
      );
    } else {
      content = (
        <EnhancedCard>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label='item inventory table'>
              <TableHead>
                <TableRow>
                  <TableCell>Icon</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'type'}
                      direction={sortField === 'type' ? sortDirection : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right'>
                    <TableSortLabel
                      active={sortField === 'quantity'}
                      direction={sortField === 'quantity' ? sortDirection : 'asc'}
                      onClick={() => handleSort('quantity')}
                    >
                      Total Quantity
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right'>
                    <TableSortLabel
                      active={sortField === 'value'}
                      direction={sortField === 'value' ? sortDirection : 'asc'}
                      onClick={() => handleSort('value')}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <DivineOrbIcon size={16} />
                      Value (Divine Orbs)
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map(item => {
                  const unitPrice = priceMap[item.id];
                  const totalValue = typeof unitPrice === 'number' && item.quantity ? unitPrice * item.quantity : undefined;

                  return (
                    <TableRow
                      key={item.id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                      }}
                      onClick={() => handleItemClick(item)}
                    >
                      <TableCell>
                        <Avatar
                          src={convertDDSToWebp(item.properties?.ddsPath, item.game)}
                          alt={item.name}
                          variant='square'
                          sx={{ width: 32, height: 32 }}
                        >
                          {item.name.substring(0, 1)}
                        </Avatar>
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        <Typography variant='body1' fontWeight={500}>
                          {item.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' color='text.secondary'>
                          {item.item_type || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <Typography variant='body1' fontWeight={500}>
                          {item.quantity || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        {pricesLoading ? (
                          <CircularProgress size={16} />
                        ) : pricesError ? (
                          <Typography variant='body2' color='error'>
                            N/A
                          </Typography>
                        ) : typeof totalValue === 'number' && typeof unitPrice === 'number' ? (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant='body2' fontWeight='bold' color='primary.main'>
                              {totalValue.toFixed(2)} Div
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              ({unitPrice.toFixed(4)} per unit)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </EnhancedCard>
      );
    }
  } else if (status === 'failed') {
    content = (
      <Box textAlign='center' py={4}>
        <Alert
          severity='error'
          sx={{
            mb: 2,
            background: 'linear-gradient(135deg, rgba(227, 27, 35, 0.1), rgba(227, 27, 35, 0.05))',
            border: `1px solid rgba(227, 27, 35, 0.3)`,
          }}
        >
          <Typography variant='h6' sx={{ mb: 1 }}>
            Failed to load items
          </Typography>
          <Typography variant='body2'>{typeof error === 'string' ? error : JSON.stringify(error)}</Typography>
        </Alert>
      </Box>
    );
  }

  // Handlers for game and league changes
  const handleGameChange = (gameCode: string) => {
    console.log(`Game changed to: ${gameCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedGame
  };

  const handleLeagueChange = (leagueCode: string) => {
    console.log(`League changed to: ${leagueCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedLeague
  };

  return (
    <PageLayout>
      <PageHeader
        icon={<InventoryIcon sx={{ fontSize: 28 }} />}
        title='Item Inventory'
        subtitle='Manage and track all items with quantities aggregated across all characters'
      />

      {/* Portfolio Summary Stats */}
      {totalPortfolioValue > 0 && (
        <ContentSection title='Portfolio Overview' sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatsCard
                title='Total Portfolio Value'
                value={`${totalPortfolioValue.toFixed(2)} Div`}
                icon={<AttachMoneyIcon />}
                color='#F39C12'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatsCard title='Unique Items' value={items.length} icon={<InventoryIcon />} color='#3498DB' />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <StatsCard
                title='Total Quantity'
                value={items.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                icon={<SearchIcon />}
                color='#27AE60'
              />
            </Grid>
          </Grid>
        </ContentSection>
      )}

      <ContentSection title='Filters & Search'>
        <EnhancedCard variant='outlined' sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <Box sx={{ flex: '1 1 300px' }}>
              <GameLeagueSelector onGameChange={handleGameChange} onLeagueChange={handleLeagueChange} />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <TextField
                label='Search by Item Name'
                variant='outlined'
                fullWidth
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
            </Box>
          </Box>

          {/* Display pricing errors if any */}
          {pricesError && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Pricing data unavailable: {pricesError}
            </Alert>
          )}
        </EnhancedCard>
      </ContentSection>

      <ContentSection title='Items List'>{content}</ContentSection>

      {/* Item Details Panel */}
      <ItemDetailsPanel open={detailsPanelOpen} onClose={handleCloseDetailsPanel} item={selectedItem} />
    </PageLayout>
  );
};

export default ItemInventoryPage;
